// pragmaVersions.json
const PRAGMA_VERSIONS = {
  title: "Pragma Solidity — Versiones, Rangos y Buenas Prácticas",
  sections: [
    {
      id: "hero",
      heading: "Pragma Solidity — Versiones, Rangos y Buenas Prácticas",
      text: `Todo contrato en Solidity debe comenzar con una sentencia 'pragma solidity'. 
Esto asegura que el compilador entienda en qué versión está escrito tu código. 
Sin pragma, corres el riesgo de que se compile con una versión no compatible.`,
      note: `Importante: 'pragma' NO instala el compilador; solo declara compatibilidad. 
La versión real se fija en tu toolchain (Hardhat/Foundry/Remix).`
    },

    {
      id: "concepto-basico",
      heading: "1) Concepto básico de la versión",
      list: [
        "El código fuente en Solidity siempre empieza con 'pragma solidity ...;'",
        "Esto previene problemas al compilar en futuras versiones que podrían introducir cambios incompatibles.",
        "Si no lo especificas, el compilador por defecto puede ser demasiado nuevo o demasiado antiguo y tu contrato fallará.",
        "Cada línea de código en Solidity debe terminar con ';' para evitar errores de sintaxis."
      ],
      code: `// Ejemplo básico
pragma solidity ^0.4.0;   // válido de 0.4.0 hasta <0.5.0
pragma solidity >=0.4.4 <0.7.0;`
    },

    {
      id: "que-es",
      heading: "2) ¿Qué es y qué NO es?",
      text: `pragma solidity es una declaración de compatibilidad con versiones del compilador. 
No descarga ni selecciona el compilador: eso se define en la configuración de build o en Remix.`,
      code: `// COMPILA solo si el compilador satisface el rango
pragma solidity ^0.8.24;`
    },

    {
      id: "semver",
      heading: "3) SemVer en Solidity (resumen express)",
      text: `Solidity sigue SemVer: MAJOR.MINOR.PATCH (ej. 0.8.24).
Cambios de MAJOR pueden romper compatibilidad; MINOR/PATCH no deberían, 
pero pueden alterar optimizaciones (codegen) y gas.`
    },

    {
      id: "exacta",
      heading: "4a) Versión exacta",
      text: `Útil para builds 100% deterministas cuando el toolchain también fija esa versión exacta.`,
      code: `pragma solidity 0.8.24;`
    },

    {
      id: "caret",
      heading: "4b) Caret ^ (misma major)",
      text: `Permite cualquier 0.8.x desde el mínimo indicado hasta <0.9.0.`,
      code: `pragma solidity ^0.8.4;`
    },

    {
      id: "rango-explicito",
      heading: "4c) Rango explícito",
      text: `Similar a ^, pero más claro y con techo explícito. 
Muy usado en librerías para definir compatibilidad.`,
      code: `pragma solidity >=0.8.4 <0.9.0;`
    },

    {
      id: "solo-min",
      heading: "4d) Solo límite inferior >= (⚠️ NO recomendado)",
      text: `Acepta futuras majors (0.9.x/1.x) que podrían romper compatibilidad y reproducibilidad.`,
      code: `pragma solidity >=0.8.4;`
    },

    {
      id: "comparadores",
      heading: "4e) Otros comparadores y combinaciones",
      codes: [
`pragma solidity >0.8.4;`,
`pragma solidity <=0.8.26;`,
`pragma solidity >0.8.4 <=0.8.26;`
      ]
    },

    {
      id: "por-que-no",
      heading: "5) ¿Por qué >= “a secas” NO es recomendable?",
      list: [
        "Riesgo de futuras MAJOR con cambios incompatibles.",
        "Pérdida de reproducibilidad entre devs y CI.",
        "Fallas al verificar bytecode en exploradores si compilas distinto.",
        "Diferencias de optimizador/Yul y costos de gas entre versiones.",
        "Deriva de dependencias aceptadas por el rango sin darte cuenta."
      ],
      note: `Regla práctica: NO uses >= “a secas” en producción. 
Prefiere ^ o un rango explícito con techo (<0.9.0).`
    },

    {
      id: "apps",
      heading: "6) Buenas prácticas en Apps/Productos",
      list: [
        "Fija versión exacta del compilador en el toolchain (Hardhat/Foundry).",
        "En el contrato, usa ^ o rango explícito acotado a la misma major."
      ],
      codes: [
`// hardhat.config.ts
const config: HardhatUserConfig = {
  solidity: "0.8.24"
};`,
`pragma solidity >=0.8.24 <0.9.0;`
      ]
    },

    {
      id: "libs",
      heading: "7) Buenas prácticas en Librerías",
      list: [
        "Usa rango explícito: >=0.8.min <0.9.0 para permitir upgrades menores/patch.",
        "Documenta la versión mínima probada y el motivo del techo."
      ],
      note: `Mantén tests en múltiples versiones dentro del rango para evitar regresiones.`
    },

    {
      id: "patrones",
      heading: "8) Patrones recomendados vs. a evitar",
      codes: [
`pragma solidity ^0.8.24;   // ✅ app con toolchain fijo`,
`pragma solidity >=0.8.19 <0.9.0; // ✅ lib con techo claro`,
`pragma solidity 0.8.24;          // ✅ determinismo total`,
`pragma solidity >=0.8.0;         // ❌ sin techo`,
`pragma solidity >=0.8.0 <=1.0.0; // ❌ abarca otra major`
      ],
      note: `Evita mezclar en el mismo repo contratos con rangos incompatibles.`
    },

    {
      id: "migracion",
      heading: "9) Migrar de 0.8.A a 0.8.B (checklist)",
      list: [
        "Actualiza tu toolchain a la NUEVA exacta (p. ej., 0.8.26).",
        "Ajusta el pragma con nuevo rango/techo apropiado.",
        "Recompila, corre tests y revisa gas/bytecode.",
        "Reaudita puntos sensibles (aritmética, assembly, libs)."
      ]
    },

    {
      id: "errores",
      heading: "10) Errores comunes y cómo detectarlos",
      list: [
        "Bytecode no coincide al verificar → compilador distinto al de despliegue.",
        "Builds diferentes entre dev/CI → faltaba fijar solc exacto en toolchain.",
        "Warnings de compatibilidad → tu pragma no cubre el compilador actual."
      ]
    },

    {
      id: "faq",
      heading: "11) FAQ rápidas",
      list: [
        "¿Puedo usar siempre la última versión? → Solo si toolchain y dependencias están alineados y testeados/auditados.",
        "¿Exacta o rango? → Toolchain exacto + rango acotado en contratos equilibra seguridad y flexibilidad.",
        "¿Por qué cambia el gas entre minors? → Cambios de optimización/codegen; fija compilador exacto en builds críticos."
      ]
    },

    {
      id: "cierre",
      heading: "Cierre",
      text: `En síntesis: evita >= a secas, fija el compilador en tu toolchain y usa ^ o rangos explícitos con techo.
Ganarás reproducibilidad, verificaciones limpias y menos sorpresas en producción.`
    }
  ]
};

export default PRAGMA_VERSIONS;
